import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/api";

export const DEFAULT_SETTINGS = {
  hero_overline: "An interdisciplinary studio — Mangalore",
  hero_line_1: "Where art, architecture,",
  hero_line_2: "material and making come together.",
  home_about_lines:
    "IKSHA is an interdisciplinary art and\narchitecture studio exploring the relationship\nbetween space, material, craft and\nartistic expression.",
  about_intro:
    "IKSHA approaches architecture as a relationship between people, place, climate, craft and material — while allowing art to remain an integral part of the design process. The studio moves between buildings and canvases, workshops and objects, with the same raw, handmade intent.",
  studio_text_1:
    "Founded and led from Mangalore, IKSHA works independently and in collaboration — with clients, craftspeople, artists and institutions. The studio takes on architecture commissions, freelance design work, art commissions and teaching formats.",
  studio_text_2:
    "Sustainability is not a service line but a working method: local material, passive climate response, and building less but better.",
  contact_email: "iksha.yaksh@gmail.com",
  contact_phone: "8660986429",
  contact_instagram: "@iksha.studio",
  contact_location: "Mangalore, India",
};

let cache = null;
let promise = null;
const listeners = new Set();

function load() {
  if (!promise) {
    promise = api
      .get("/settings")
      .then((r) => {
        cache = { ...DEFAULT_SETTINGS, ...r.data };
        listeners.forEach((f) => f(cache));
      })
      .catch(() => {
        cache = { ...DEFAULT_SETTINGS };
      });
  }
  return promise;
}

export function refreshSettings() {
  promise = null;
  load();
}

const pageCache = {};
const pagePromises = {};
const pageListeners = {};

function previewSlug() {
  try {
    return new URLSearchParams(window.location.search).get("preview_draft");
  } catch {
    return null;
  }
}

export function usePage(slug) {
  const [data, setData] = useState(pageCache[slug] || {});
  useEffect(() => {
    if (!pagePromises[slug]) {
      const draft = previewSlug() === slug ? "?draft=1" : "";
      pagePromises[slug] = api
        .get(`/pages/${slug}${draft}`)
        .then((r) => {
          pageCache[slug] = r.data;
          (pageListeners[slug] || []).forEach((f) => f(r.data));
        })
        .catch(() => {
          pageCache[slug] = {};
        });
    }
    const f = (v) => setData(v);
    (pageListeners[slug] = pageListeners[slug] || []).push(f);
    if (pageCache[slug]) setData(pageCache[slug]);
    return () => {
      pageListeners[slug] = (pageListeners[slug] || []).filter((x) => x !== f);
    };
  }, [slug]);
  return data;
}

export function refreshPages() {
  Object.keys(pagePromises).forEach((k) => delete pagePromises[k]);
}

export function useSettings() {
  const [s, setS] = useState(cache || DEFAULT_SETTINGS);
  useEffect(() => {
    load();
    const f = (v) => setS(v);
    listeners.add(f);
    return () => listeners.delete(f);
  }, []);
  return s;
}

const SITE_TEXT_FIELDS = [
  { name: "hero_overline", label: "Hero — small line above logo" },
  { name: "hero_line_1", label: "Hero statement — line 1" },
  { name: "hero_line_2", label: "Hero statement — line 2" },
  { name: "home_about_lines", label: "Homepage About statement (one phrase per line)", type: "textarea" },
  { name: "about_intro", label: "About page — intro paragraph", type: "textarea" },
  { name: "studio_text_1", label: "About page — studio box, paragraph 1", type: "textarea" },
  { name: "studio_text_2", label: "About page — studio box, paragraph 2", type: "textarea" },
  { name: "contact_email", label: "Contact email" },
  { name: "contact_phone", label: "Phone / WhatsApp" },
  { name: "contact_instagram", label: "Instagram handle" },
  { name: "contact_location", label: "Studio location" },
];

export function SiteTextTab() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  if (!form) return <p className="font-mono text-sm text-ink/40">Loading…</p>;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/settings", form);
      refreshSettings();
      toast.success("Site text updated — live everywhere.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-paper border border-ink/20 focus:border-brand outline-none px-3 py-2 text-sm";

  return (
    <form onSubmit={save} data-testid="site-text-form" className="max-w-2xl space-y-5">
      <p className="text-sm text-ink/60 mb-2">
        Edit the words on the homepage hero, About page and contact details. Changes go live instantly.
      </p>
      {SITE_TEXT_FIELDS.map((f) => (
        <div key={f.name}>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 mb-1.5">{f.label}</label>
          {f.type === "textarea" ? (
            <textarea rows={3} value={form[f.name] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
              className={`${inputCls} resize-y`} data-testid={`site-field-${f.name}`} />
          ) : (
            <input value={form[f.name] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
              className={inputCls} data-testid={`site-field-${f.name}`} />
          )}
        </div>
      ))}
      <button type="submit" disabled={busy} data-testid="site-text-save-btn"
        className="bg-ink text-paper px-8 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand transition-colors disabled:opacity-50">
        {busy ? "Saving…" : "Save Site Text"}
      </button>
    </form>
  );
}
